// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from '@mui/icons-material';
import { Box, MenuItem, Paper, Portal, Select } from '@mui/material';

export const NestedSelect = ({
  value,
  onChange,
  options = [],
  disabled = false,
  fullWidth = true,
  testId,
  selectedAssetClassAttrsList = [],
  referentielConsequences = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [isChildHovered, setIsChildHovered] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const shouldPreventCloseRef = useRef(false);

  const renderSelectedValue = useMemo(() => {
    return (selected) => {
      if (selected == null || options == null) return selected ?? '';
      const option = options.find((opt) => (opt?.value ?? opt) === selected);
      return option?.label ?? selected;
    };
  }, [options]);

  const processedOptions = useMemo(() => {
    const computeChildOptionsInternal = (parentValue) => {
      if (!parentValue) return [];
      if (parentValue.startsWith('CustomData')) {
        const customDataType = parentValue.startsWith('CustomDataNumber')
          ? 'CustomDataNumber'
          : parentValue.startsWith('CustomDataString')
            ? 'CustomDataString'
            : parentValue.startsWith('CustomDataGroupNumber')
              ? 'CustomDataGroupNumber'
              : parentValue.startsWith('CustomDataGroupString')
                ? 'CustomDataGroupString'
                : 'CustomData';
        return selectedAssetClassAttrsList.filter((attr) => attr.startsWith(customDataType));
      }
      if (parentValue.startsWith('ConsequenceQuantity') || parentValue.startsWith('SumOfConsequenceQuantities')) {
        return referentielConsequences.map((attr) => attr.Consequence);
      }
      return [];
    };

    return (
      options?.map((option) => {
        const optionValue = option?.value ?? option;
        const optionLabel = option?.label ?? option;
        const isWildcard = optionLabel?.endsWith('.*') || optionLabel?.includes('.*');

        if (isWildcard) {
          const children = computeChildOptionsInternal(optionValue);
          const isDisabledParent = children.length === 0;
          const hasSelectedChild = value && value.startsWith(optionValue.replace('.*', '.').replace('*', ''));
          const isParentSelected = !!hasSelectedChild;

          return {
            type: 'wildcard',
            optionValue,
            optionLabel,
            children,
            isDisabledParent,
            isParentSelected,
          };
        }

        return {
          type: 'simple',
          optionValue,
          optionLabel,
        };
      }) || []
    );
  }, [options, value, selectedAssetClassAttrsList, referentielConsequences]);

  const handleParentMouseEnter = useCallback(
    (event, optionValue) => {
      // Find the processed option to get the children
      const processedOption = processedOptions.find((opt) => opt.optionValue === optionValue);
      const children = processedOption?.children || [];
      const disabledParent = children.length === 0;
      if (disabledParent) return;
      setAnchorEl(event.currentTarget);
      setHoveredOption(optionValue);
    },
    [processedOptions]
  );

  const handleChildSelection = useCallback(
    (childValue) => {
      const event = {
        target: { value: childValue, name: undefined },
        currentTarget: { value: childValue },
      };
      onChange(event);
      setAnchorEl(null);
      setHoveredOption(null);
      setSelectOpen(false);
    },
    [onChange]
  );

  const computeChildValueAndLabel = useCallback((attr, optionLabel) => {
    let childValue;
    let childLabel;

    if (optionLabel === 'SumOfConsequenceQuantities.*') {
      childValue = `SumOfConsequenceQuantities.${attr}`;
      childLabel = attr;
    } else if (optionLabel === 'ConsequenceQuantity.*') {
      childValue = `ConsequenceQuantity.${attr}`;
      childLabel = attr;
    } else {
      childValue = attr;
      childLabel = attr.includes('.') ? attr.split('.').pop() : attr;
    }

    return { childValue, childLabel };
  }, []);

  const renderChildMenuItem = useCallback(
    (attr, optionLabel) => {
      const { childValue, childLabel } = computeChildValueAndLabel(attr, optionLabel);
      const isChildSelected = value === childValue;

      return (
        <MenuItem
          key={childValue}
          value={childValue}
          onClick={(e) => {
            e.stopPropagation();
            handleChildSelection(childValue);
          }}
          sx={{
            backgroundColor: isChildSelected ? 'action.selected' : 'transparent',
            '&:hover': {
              backgroundColor: isChildSelected ? 'action.selected' : 'action.hover',
            },
          }}
        >
          {childLabel}
        </MenuItem>
      );
    },
    [value, computeChildValueAndLabel, handleChildSelection]
  );

  const handleParentMouseLeave = useCallback(() => {
    if (!isChildHovered) {
      setAnchorEl(null);
      setHoveredOption(null);
    }
  }, [isChildHovered]);

  const handleChildMouseEnter = useCallback(() => {
    setIsChildHovered(true);
  }, []);

  const handleChildMouseLeave = useCallback(
    (event) => {
      const relatedTarget = event.relatedTarget;
      const isMovingToParent = relatedTarget && relatedTarget === anchorEl;
      setIsChildHovered(false);
      if (!isMovingToParent) {
        setAnchorEl(null);
        setHoveredOption(null);
      }
    },
    [anchorEl]
  );

  const renderSimpleOption = useCallback((processedOption) => {
    const { optionValue, optionLabel } = processedOption;
    return (
      <MenuItem key={optionValue} data-cy={optionValue || optionLabel} value={optionValue}>
        {optionLabel}
      </MenuItem>
    );
  }, []);

  const renderWildcardOption = (processedOption) => {
    const { optionValue, optionLabel, children, isDisabledParent, isParentSelected } = processedOption;
    const isCurrentlyHovered = hoveredOption === optionValue;

    return (
      <MenuItem
        key={optionValue}
        data-cy={optionValue || optionLabel}
        value={optionValue}
        disabled={isDisabledParent}
        onMouseEnter={(e) => handleParentMouseEnter(e, optionValue)}
        onMouseLeave={handleParentMouseLeave}
        sx={{
          position: 'relative',
          backgroundColor: isDisabledParent
            ? 'transparent'
            : isCurrentlyHovered
              ? 'action.hover'
              : isParentSelected
                ? 'action.hover'
                : 'transparent',
          color: isDisabledParent ? 'text.disabled' : 'inherit',
          cursor: isDisabledParent ? 'not-allowed' : 'pointer',
          '&:hover': {
            backgroundColor: isDisabledParent ? 'transparent' : 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{optionLabel}</span>
          {!isDisabledParent && <ArrowRight sx={{ fontSize: 18, ml: 1 }} />}
        </Box>
        {anchorEl && isCurrentlyHovered && !isDisabledParent && (
          <Portal>
            <Paper
              onMouseEnter={handleChildMouseEnter}
              onMouseLeave={handleChildMouseLeave}
              sx={{
                position: 'fixed',
                left: anchorEl.getBoundingClientRect().right,
                top: (() => {
                  const rect = anchorEl.getBoundingClientRect();
                  const submenuHeight = children.length * 36;
                  const viewportHeight = window.innerHeight;
                  if (rect.top + submenuHeight > viewportHeight) {
                    return Math.max(0, rect.bottom - submenuHeight);
                  }
                  return rect.top;
                })(),
                backgroundColor: 'background.paper',
                boxShadow: 2,
                minWidth: '120px',
                zIndex: 9999,
              }}
            >
              {children.map((attr) => renderChildMenuItem(attr, optionLabel))}
            </Paper>
          </Portal>
        )}
      </MenuItem>
    );
  };

  return (
    <Box>
      <Select
        value={value || ''}
        displayEmpty={true}
        data-testid={testId}
        onChange={(e) => {
          if (e.target.value?.endsWith('.*')) {
            shouldPreventCloseRef.current = true;
            return;
          }
          shouldPreventCloseRef.current = false;
          onChange(e);
        }}
        fullWidth={fullWidth}
        disabled={disabled}
        open={selectOpen}
        onClose={() => {
          if (shouldPreventCloseRef.current) {
            shouldPreventCloseRef.current = false;
            return;
          }
          setSelectOpen(false);
          setAnchorEl(null);
          setHoveredOption(null);
        }}
        onOpen={() => setSelectOpen(true)}
        renderValue={renderSelectedValue}
      >
        {processedOptions.map((processedOption) => {
          return processedOption.type === 'wildcard'
            ? renderWildcardOption(processedOption)
            : renderSimpleOption(processedOption);
        })}
      </Select>
    </Box>
  );
};

NestedSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  testId: PropTypes.string,
  selectedAssetClassAttrsList: PropTypes.array,
  referentielConsequences: PropTypes.array,
};

export default NestedSelect;
